import { Link, navigate } from "gatsby";
import React, { useState } from "react";
import moment from "moment";
import "moment/locale/bg";

import InfoIcon from "@icons/info_icon.svg";
import CheckDataIcon from "@icons/check-data-icon.svg";
import CarCrashIcon from "@icons/car_crash_icon.svg";
import CheckIcon from "@icons/check_icon_new.svg";
import CarVinIcon from "@icons/car_vin_icon.svg";

import MyPoliciesIcon from "@icons/MyProfile_MyPolicies_icon.svg";
import MyVignetteIcon from "@icons/MyProfile_MyVignette_icon.svg";
import ServicesIcon from "@icons/MyProfile_services_icon.svg";
import ClaimsIcon from "@icons/MyProfile_claims_icon.svg";
import ProfileDataIcon from "@icons/MyProfile_profile_icon.svg";
import InshuredIcon from "@icons/MyProfile_inshured_icon.svg";
import AddressIcon from "@icons/MyProfile_address_icon.svg";
import CardIcon from "@icons/MyProfile_card_icon.svg";
import ExitIcon from "@icons/MyProfile_exit_icon.svg";

import RenewalIcon from "@images/icons/renewal.svg";
import TabBar from "@components/common/TabBar";
import GloInfoBox from "@components/common/GloInfoBox";

import { Colors } from "../../styles/colors";
import { getSiteSuggestions } from "../../services/AutocompleteService";
import * as styles from "../../styles/modules/Policies.module.sass";
import Notifications from "../../components/common/Notifications";
import ArrowRight from "../../images/icons/right_arrow.svg";
import { bgPhonePrefix } from "../activeProducts/helpers";
import { formatCity, getCurrencyByCode, getCountriesByCode, isFalsy, getCurrencyValueByKey, getEarliestRenewalDate, EUR_FIXED_RATE } from "../../utils/functions";
import { IGNORE_SECONDARY_CURRENCY, PRIMARY_CURRENCY, SECONDARY_CURRENCY } from "../env";
import LazySVGS from "../../images/LazySVGS";

export const profileOptionsConfigFnc = ({ lngPart }) => ({
  optionPolicies: {
    title: "pages.common.policyAndPayments",
    subTitle: "pages.common.infoAboutYourPoliciesAndPayments",
    icon: "PolicyPayments",
    onClickNavigation: () => navigate(`${lngPart}/policies`),
    alwaysActive: true,
    tanantDisplayConfig: {
      boleron: true,
      vivacom: true
    }
  },
  option1: {
    title: "pages.common.vignettesAndServices",
    subTitle: "pages.common.statusVignetteCivilGtpsandFines",
    to: `${lngPart}/my-profile-page/vignettes-and-services`,
    icon: "CarExclamation",
    tanantDisplayConfig: {
      boleron: true,
      vivacom: false
    }
  },
  option2: {
    title: "pages.common.profileData",
    subTitle: "pages.common.personalDataInsuredAndVehicles",
    to: `${lngPart}/my-profile-page/profile-data`,
    icon: "User",
    tanantDisplayConfig: {
      boleron: true,
      vivacom: true
    }
  }
});

// Flat "My Profile" menu (redesign): every destination is listed directly on the
// hub under section headers instead of the old two-level menu. Each item links
// straight to its leaf page. `tenants` gates visibility per tenant, `dataCheck`
// disables the row when the user has no related data.
export const profileMenuSectionsFnc = ({ lngPart }) => {
  const base = `${lngPart}/my-profile-page`;

  return [
    {
      key: "top",
      items: [{ key: "policies", tKey: "myProfileMenu.policies", tDefault: "Полици и плащания", Icon: MyPoliciesIcon, to: `${lngPart}/policies`, tenants: { boleron: true, vivacom: true } }]
    },
    {
      key: "services",
      titleKey: "pages.common.vignettesAndServices",
      titleDefault: "Винетки и услуги",
      items: [
        { key: "vignettes", tKey: "pages.common.myVignettes", tDefault: "Моите винетки", Icon: MyVignetteIcon, to: `${base}/vignettes-and-services/vignettes`, tenants: { boleron: true, vivacom: false } },
        {
          key: "fineChecks",
          tKey: "myProfileMenu.fineChecks",
          tDefault: "Проверки за ГО, ГТП, винетка и глоби",
          Icon: ServicesIcon,
          to: `${base}/vignettes-and-services/fine-checks`,
          tenants: { boleron: true, vivacom: false }
        },
        { key: "damageChecks", tKey: "myProfileMenu.damageChecks", tDefault: "Проверки за Щети", Icon: ClaimsIcon, to: `${base}/vignettes-and-services/damage-checks`, tenants: { boleron: true, vivacom: false } }
      ]
    },
    {
      key: "profileData",
      titleKey: "myProfileMenu.profileSection",
      titleDefault: "Данни за профила",
      items: [
        { key: "personalData", tKey: "pages.common.personalData", tDefault: "Лични данни", Icon: ProfileDataIcon, to: `${base}/profile-data/personal-data`, tenants: { boleron: true, vivacom: true } },
        {
          key: "insuredObjects",
          tKey: "myProfileMenu.insuredObjects",
          tDefault: "Застраховани лица/МПС",
          Icon: InshuredIcon,
          to: `${base}/profile-data/insured-objects`,
          tenants: { boleron: true, vivacom: true },
          dataCheck: "insured"
        },
        {
          key: "deliveryAddresses",
          tKey: "pages.common.deliveryAddresses",
          tDefault: "Адреси за доставка",
          Icon: AddressIcon,
          to: `${base}/profile-data/delivery-addresses`,
          tenants: { boleron: true, vivacom: true },
          dataCheck: "delivery"
        },
        { key: "savedCards", tKey: "pages.common.savedCards", tDefault: "Запазени карти", Icon: CardIcon, to: `${base}/profile-data/saved-cards`, tenants: { boleron: true, vivacom: true }, dataCheck: "cards" }
      ]
    }
  ];
};

export const isProfileMenuItemDisabled = (dataCheck, initLoadedData) => {
  if (!dataCheck || !initLoadedData) {
    return false;
  }
  if (dataCheck === "insured") {
    return (initLoadedData.initPersons?.length || 0) === 0 && (initLoadedData.initCars?.length || 0) === 0;
  }
  if (dataCheck === "delivery") {
    return !initLoadedData.initDeliveryData || Object.keys(initLoadedData.initDeliveryData || {}).length === 0;
  }
  if (dataCheck === "cards") {
    return (initLoadedData.initCards?.length || 0) === 0;
  }
  return false;
};

const userSubMenuOptionsConfig = {
  option1: {
    title: "pages.common.personalData",
    subTitle: "pages.common.reviewAndEditYourPersonalData",
    alwaysActive: true
  },
  option2: {
    title: "pages.common.insuredPersonsUppercase",
    subTitle: "pages.common.reviewAndEditInsuredPeopleAndVehicles"
  },
  option3: {
    title: "pages.common.deliveryAddresses",
    subTitle: "pages.common.infoYourDeliveryAddresses"
  },
  option4: {
    title: "pages.common.savedCards",
    subTitle: "pages.common.reviewAndEditYourPaymentCards"
  }
};

const vignettesAndServicesSubMenuOptionsConfig = {
  option1: {
    title: "pages.common.myVignettes",
    alwaysActive: true
  },
  option2: {
    title: "pages.common.finesCheckUppercase",
    subTitle: "pages.common.statusOfCivilGtpsVignetteAndFines",
    alwaysActive: true
  },
  option3: {
    title: "pages.common.damageCheck",
    alwaysActive: true
  }
};

export const subMenuOptionConfigs = {
  "profile-data": userSubMenuOptionsConfig,
  "vignettes-and-services": vignettesAndServicesSubMenuOptionsConfig
};

export const msgObject = {
  saveWarning: "pages.common.dataWillBeDeletedAfterSave",
  peopleInfo: "pages.common.noInsuredPersonsYet",
  carsInfo: "pages.common.noInsuredVehiclesYet"
};

export const initDisableSaveBtnObj = {
  marketingConsent: true,
  firstName: true,
  middleNames: true,
  lastName: true,
  egn: true,
  email: true,
  phone: true,
  address: true,
  deliveryData: true,
  city: true,
  cityData: true,
  cars: true,
  persons: true,
  cards: true
};

export const initialPersonalData = {
  email: "",
  firstName: "",
  middleNames: "",
  surameName: "",
  egn: null,
  mobileNumber: null,
  city: null,
  address: ""
};

export const initialPersonalDataErros = {
  email: "",
  firstName: "",
  middleNames: "",
  surameName: "",
  egn: "",
  mobileNumber: "",
  city: "",
  permanentAddress: ""
};

export const myProfileTxtFields = [
  {
    name: "email",
    label: "pages.common.email",
    classes: "is-fullwidth",
    isReadOnly: true,
    infoContent: (
      <span
        style={{ marginBottom: -8, border: "none" }}
        className="has-tooltip-multiline has-tooltip-adjust-mobile"
        data-translation="pages.common.changeYourEmail"
        data-tooltip="Ако желаеш да промениш имейл адреса си, може да го направиш като се свържеш с нас."
      >
        <InfoIcon className="info-icon-small" />
      </span>
    )
  },
  {
    classes: "is-fullwidth",
    autoComplete: "given-name",
    name: "firstName",
    label: "pages.common.firstName",
    placeholder: "pages.common.firstName",
    maxLength: 25
  },
  {
    classes: "is-fullwidth",
    autoComplete: "additional-name",
    name: "middleNames",
    label: "pages.common.middleName",
    placeholder: "pages.common.middleName",
    maxLength: 25
  },
  {
    classes: "is-fullwidth",
    autoComplete: "family-name",
    name: "lastName",
    label: "pages.common.lastName",
    placeholder: "pages.common.lastName",
    maxLength: 30
  },
  {
    name: "egn",
    classes: "is-fullwidth",
    autoComplete: "family-name",
    label: "pages.common.egnOrLnch",
    placeholder: "pages.common.egnExample",
    type: "number",
    pattern: "[0-9]*",
    inputmode: "numeric",
    maxLength: 30
  },
  {
    name: "mobileNumber",
    prefix: bgPhonePrefix,
    classes: "is-fullwidth",
    className: "is-expanded select-autocomplete-control",
    resultsClassName: "select-autocomplete",
    label: "pages.common.mobilePhone",
    placeholder: "pages.common.phoneExample",
    autoComplete: "family-name",
    type: "tel",
    inputmode: "tel"
  },
  {
    name: "city",
    label: "pages.common.city",
    placeholder: "pages.common.searchCity",
    className: "is-expanded select-autocomplete-control",
    minSearchChars: 2,
    isSelectAutocomplete: true,
    isWithLeftIcon: true,
    autocomplete: (search, formContext) => getSiteSuggestions(formContext.token, search),
    labelField: e => formatCity(e),
    clearFormData: () => ({
      prpCity: "",
      prpCityFormatedString: "",
      prpCityPostCode: "",
      prpDesc: 0,
      prpStreet: "",
      prpType: 0
    })
  },
  {
    name: "address",
    label: "pages.common.permanentAddress",
    placeholder: `pages.common.addressPlaceholder`,
    classes: "is-fullwidth",
    maxLength: 90
  }
];

// Key is the optionValue - value is the data name
export const dataByOptionValueConfig = {
  1: ["initPersons", "initCars"],
  2: "initDeliveryData",
  3: "initCards"
};

export const isBtnDissabled = formObj => Object.values(formObj).every(field => field);

export const setTitle = ({ menuOption, subMenuOption, userEmail, carDamageInfoToDisplay, gloInfoToDisplay, t }) => {
  const profileDataTitles = [t("pages.common.personalData"), t("pages.common.insuredPersons_plural"), t("pages.common.deliveryAddresses"), t("pages.common.savedCards")];
  const vignTitles = [t("pages.common.myVignettes"), t("pages.common.fineChecks"), t("pages.common.damageChecks")];

  switch (menuOption) {
    case 0:
      return userEmail;
    case "profile-data":
      if (subMenuOption === 0) {
        return t("pages.common.profileData");
      }
      return profileDataTitles[subMenuOption - 1] || "";
    case "vignettes-and-services":
      if (subMenuOption === 0) return t("pages.common.vignettesAndServices");
      if (subMenuOption === 2 && gloInfoToDisplay?.lastVerifiedDate) return `${t("check")} - ${moment(gloInfoToDisplay.lastVerifiedDate).format("DD.MM.YYYY")}`;
      if (subMenuOption === 3 && carDamageInfoToDisplay) return t("pages.common.damageInformation");
      return vignTitles[subMenuOption - 1] || "";
    default:
      return "";
  }
};

export const hashCode = str => {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    const chr = str.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + chr;
    // eslint-disable-next-line no-bitwise
    hash |= 0; // Convert to 32bit integer
  }

  return hash < 0 ? (hash * -1).toString() : hash.toString();
};

// Current Options that can have no data and need menu option disabling (3 - addresses , 4 - cards)
export const disableMenuOptionOnNoData = (optionValue, dataToCheck) => {
  let isOptionDisabled;

  const dataNames = dataByOptionValueConfig[optionValue];
  const data = dataToCheck[dataNames];

  if (Array.isArray(dataNames)) {
    const data1 = dataToCheck[dataNames[0]];
    const data2 = dataToCheck[dataNames[1]];

    return data1.length === 0 && data2.length === 0;
  }

  if (!data) {
    return true;
  }

  const isDataArray = Array.isArray(data);
  const isDataObject = typeof data === "object" && data !== null && !Array.isArray(data);

  if (isDataObject) {
    isOptionDisabled = Object.keys(data).length === 0;
  } else if (isDataArray) {
    isOptionDisabled = data.length === 0;
  } else {
    isOptionDisabled = Boolean(data);
  }

  return isOptionDisabled;
};

export const formatDeliveryAddress = deliveryDataParam => {
  let address = deliveryDataParam.ulDelivery || "";

  if (deliveryDataParam.noDelivery) {
    address += ` ${deliveryDataParam.noDelivery}`;
  }

  if (deliveryDataParam.blDelivery) {
    address += ` бл:${deliveryDataParam.blDelivery}`;
  }

  if (deliveryDataParam.vhDelivery) {
    address += ` вх:${deliveryDataParam.vhDelivery}`;
  }

  if (deliveryDataParam.etDelivery) {
    address += ` eт:${deliveryDataParam.etDelivery}`;
  }

  if (deliveryDataParam.apDelivery) {
    address += ` ап:${deliveryDataParam.apDelivery}`;
  }

  return address;
};

// Helper Components
export const NoItemsMsg = ({ type, text }) => (
  <Notifications classes={type === "is-primary" ? "notification-default" : "notification-warning"} svgIcon="ExclamationMark" message={text} type={type} subType="is-light" />
);

export const ProfileSectionTitle = ({ children }) => <h2 className="my-profile-section-title">{children}</h2>;

export const ProfileMenuRow = ({ Icon, label, to, isDisabled }) => {
  const content = (
    <div className={`${styles.myProfileActions} my-profile-menu-row ${isDisabled ? styles.isDisabled : ""}`}>
      <div className="is-flex is-align-items-center">
        <Icon className="my-profile-menu-icon" />
        <span className="my-profile-menu-label">{label}</span>
      </div>
      <div className={styles.arrowCircle}>
        <ArrowRight className={styles.rightArrow} />
      </div>
    </div>
  );

  if (isDisabled) {
    return content;
  }

  return (
    <Link className="is-fullwidth" to={to}>
      {content}
    </Link>
  );
};

export const ProfileExitRow = ({ label, onClick }) => (
  <div className="my-profile-exit-row is-clickable" onClick={onClick}>
    <ExitIcon className="my-profile-menu-icon" />
    <span className="my-profile-exit-label">{label}</span>
  </div>
);

export const ProfileOptions = ({ title, subTitle, icon, to, isDisabled, onClickNavigation, t }) => {
  const children = (
    <div className={`${styles.myProfileActions} ${isDisabled ? styles.isDisabled : ""} is-fullwidth profileOption`} onClick={onClickNavigation}>
      <div className="is-flex is-align-items-center ">
        <LazySVGS svg={icon} style={{ minWidth: 30 }} fallback={<div />} />
        <div className="ml-4">
          <h5>{t(title)}</h5>
          <p className="is-size-5" style={{ maxWidth: 340 }}>
            {t(subTitle)}
          </p>
        </div>
      </div>
      <div className={styles.arrowCircle}>
        <ArrowRight className={styles.rightArrow} />
      </div>
    </div>
  );

  if (isDisabled || onClickNavigation) {
    return children;
  }

  return <Link to={to}>{children}</Link>;
};

export const SubMenuOptions = ({ title, subTitle, to, isDisabled, t }) => {
  const children = (
    <div className={`${styles.myProfileActions} ${isDisabled ? styles.isDisabled : ""} is-fullwidth`}>
      <div className="ml-4">
        <h5>{t(title)}</h5>
        <p className="is-size-5" style={{ maxWidth: 340 }}>
          {t(subTitle)}
        </p>
      </div>
      <div className={styles.arrowCircle}>
        <ArrowRight className={styles.rightArrow} />
      </div>
    </div>
  );

  if (isDisabled) {
    return children;
  }

  return (
    <Link className="is-fullwidth" to={to}>
      {children}
    </Link>
  );
};

export const ChrContent = ({ carDamageData, basePath = "", t }) => {
  if (isFalsy(carDamageData)) {
    return (
      <div className="is-flex is-flex-direction-column is-align-items-center row-gap-12 has-text-centered">
        <img src="/images/boleron/landing/crh.svg" style={{ width: 115, height: 115 }} alt="Проверка в Кат" />
        <p className="responsive-font-size-3 is-bold-family">{t("noSearchMade", "НЯМАШ НАПРАВЕНА ПРОВЕРКА")}</p>

        <p
          style={{
            margin: " 0px 30px"
          }}
        >
          {t("checkCarHistory", "Провери историята на твоя автомобил само чрез въвеждане на VIN номер или номер на талон.")}
        </p>
      </div>
    );
  }

  return (
    <div className="is-fullwidth">
      {carDamageData.map((data, index) => {
        const { extraData, timestamp } = data;

        const purchaseData = Array.isArray(data.purchaseData) ? data.purchaseData[0] : data.purchaseData;

        const { vinNumber, carNo } = purchaseData || {};
        const { AudaHistoryInfo } = extraData[0] || {};
        const { TotalNumberOfClaims } = AudaHistoryInfo || {};

        const formattedDate = moment(timestamp).format("DD.MM.YYYY г.");

        return (
          <Link className="is-fullwidth" to={`${basePath}/vignettes-and-services/damage-checks?id=${vinNumber || carNo}`} key={index}>
            <div className={`${styles.myProfileActions} is-fullwidth`}>
              <div className="is-flex is-align-items-center ">
                <CheckDataIcon />
                <div className="ml-4">
                  <p>
                    {carNo ? "Автомобил: " : "VIN номер: "}
                    <span className="is-bold-family">{carNo || vinNumber}</span>
                  </p>
                  <p>
                    {t("check", "Проверка")}: {formattedDate}
                  </p>
                  <p>
                    {t("damages", "Щети")}: {`${TotalNumberOfClaims} бр.` || "Няма информация"}{" "}
                  </p>
                </div>
              </div>
              <div className={styles.arrowCircle}>
                <ArrowRight className={styles.rightArrow} />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export const VignettesList = ({ userVignetteData, t, lngPart }) => {
  if (isFalsy(userVignetteData)) {
    return (
      <div className="is-flex is-flex-direction-column is-align-items-center row-gap-12 has-text-centered">
        <img src="/images/boleron/landing/vig.svg" style={{ width: 115, height: 115 }} alt="Винетка" />
        <p className="responsive-font-size-3 is-bold-family">{t("noVignettePurchasedYet", "ЗАСЕГА НЯМАШ ЗАКУПЕНА ВИНЕТКА")}</p>

        <p
          style={{
            margin: " 0px 55px"
          }}
        >
          {t(
            "With us you can buy your electronic vignette quickly and easily with just a few clicks.",
            "При нас може да купиш своята електронна винетка бързо и лесно само с няколко клика."
          )}
        </p>
      </div>
    );
  }

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const calculateVignetteStatus = validityEndDateTimeUTC => {
    const currentDate = moment();
    const endDate = new Date(validityEndDateTimeUTC);
    const differenceInDays = Math.ceil((endDate - currentDate) / (1000 * 60 * 60 * 24));

    if (endDate < currentDate) {
      return {
        color: Colors.GRAY_FOR_BORDER,
        text: t("key14368", "Изтекла"),
        status: "expired"
      };
    }
    if (differenceInDays <= 30) {
      return {
        color: Colors.GOLD,
        text: t("expiresInNDays", { differenceInDays }),
        status: "forRenewal"
      };
    }

    return {
      color: Colors.GREEN,
      text: t("key14365", "Активна"),
      status: "active"
    };
  };

  const { expiredVignettes, activeVignettes } = userVignetteData.reduce(
    (acc, element) => {
      if (element?.extraData?.length && element.extraData[0]?.kapschProperties?.validity?.validityEndDateTimeUTC) {
        const validityEndDateTimeUTC = new Date(element.extraData[0].kapschProperties.validity.validityEndDateTimeUTC);
        if (validityEndDateTimeUTC < moment()) {
          acc.expiredVignettes.push(element);
        } else {
          acc.activeVignettes.push(element);
        }
      }
      return acc;
    },
    { expiredVignettes: [], activeVignettes: [] }
  );
  const getValidityStartDateTimeUTC = element => element?.extraData?.[0]?.kapschProperties?.validity?.validityStartDateTimeUTC || 0;

  const sortByStartDateDesc = (a, b) => new Date(getValidityStartDateTimeUTC(b)) - new Date(getValidityStartDateTimeUTC(a));

  activeVignettes.sort(sortByStartDateDesc);
  expiredVignettes.sort(sortByStartDateDesc);

  const vignetesForDisplay = {
    0: activeVignettes,
    1: expiredVignettes
  };

  return (
    <div className="tabsWrapper" style={{ width: "100%" }}>
      <TabBar classes="mb-6" selected={Number(value)} options={[t("key14373", "АКТИВНИ"), t("key14374", "ИЗТЕКЛИ")]} handleClick={handleChange} />

      {Array.isArray(vignetesForDisplay[value]) && vignetesForDisplay[value].length === 0 && (
        <Notifications
          classes="notification-warning"
          svgIcon="ExclamationMark"
          message={t("noVigMsg", { txtMsg: value === 0 ? "активни" : "изтекли" })}
          type="is-primary"
          subType="is-light"
          marginTop={20}
        />
      )}

      {vignetesForDisplay[value].map((vigData, index) => {
        const { extraData, email } = vigData;

        const purchaseData = Array.isArray(vigData.purchaseData) ? vigData.purchaseData[0] : vigData.purchaseData;

        const { carNo, type } = purchaseData || {};
        const { kapschProperties, currencyAmount } = extraData[0];

        const { name, id: vigDurationID } = type;
        const formattedVigneteDurationTime = `${name}`.toLocaleLowerCase();

        const { price, id, validity } = kapschProperties;
        const { amount, currency } = price;
        const { amountBGN, amountEUR } = currencyAmount || {};

        const vignettePrice =
          amountBGN && amountEUR && !IGNORE_SECONDARY_CURRENCY
            ? getCurrencyValueByKey({
                priceObj: {
                  amount: amountEUR,
                  primaryCurrency: PRIMARY_CURRENCY,
                  secondaryCurrency: SECONDARY_CURRENCY,
                  amountSecondaryCurrency: SECONDARY_CURRENCY === "EUR" ? amountEUR : amountBGN
                },
                key: "amount"
              })
            : `${amount} ${getCurrencyByCode({ currencyCode: currency })}`;

        const { validityStartDateTimeUTC, validityEndDateTimeUTC } = validity;
        const formattedDate = `${moment(validityStartDateTimeUTC).format("DD.MM.YYYY г.")} - ${moment(validityEndDateTimeUTC).utc().format("DD.MM.YYYY г.")}`;

        const { color, text, status } = calculateVignetteStatus(validityEndDateTimeUTC);

        return (
          <div className={styles.vignettesContainer} key={index}>
            <div className={styles.policyInfo} style={{ padding: 0, marginBottom: 31 }}>
              <h4 className={styles.policyHeader}>{t("Vignette", "ВИНЕТКА")}</h4>
              <div>
                <div
                  className={styles.policyStatusChip}
                  style={{
                    backgroundColor: color,
                    fontSize: 11
                  }}
                >
                  {text}
                </div>
              </div>
            </div>

            <p>
              <span className="is-regular-font-family">{t("pages.common.validity")}:</span> {formattedDate}
            </p>

            <p>
              <span className="is-regular-font-family">{t("key14098", "Автомобил")}:</span> {carNo}
            </p>

            <p>
              <span className="is-regular-font-family">{t("Период")}:</span> {formattedVigneteDurationTime}
            </p>

            <p>
              <span className="is-regular-font-family">{t("vigPrice", "Цена на винетката")}:</span> {vignettePrice}
            </p>

            <p>
              <span className="is-regular-font-family">{t("vigId", "ID на винетка")}:</span> {id}
            </p>

            {status !== "active" && vigDurationID === "year" && (
              <div
                className={styles.policyActions}
                style={{ marginTop: 23 }}
                onClick={() => {
                  navigate(`${lngPart}/external-link-handler/?product=VIG&data=${btoa(`${email},${carNo},${getEarliestRenewalDate(validityEndDateTimeUTC)}`)}`);
                }}
              >
                <div className="is-flex is-align-items-center is-justify-content-space-between">
                  <RenewalIcon className={styles.policyActionsIcon} />
                  <span className="pl-2">{t("vigRenwal", "Подновяване на винетката")}</span>
                </div>
                <div className={styles.arrowCircle}>
                  <ArrowRight className={styles.rightArrow} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const GLoContent = ({ gloSavedData, basePath = "", t }) => {
  if (isFalsy(gloSavedData)) {
    return (
      <div className="is-flex is-flex-direction-column is-align-items-center row-gap-12 has-text-centered">
        <img src="/images/boleron/landing/glo.svg" style={{ width: 115, height: 115 }} alt="Глоби" />
        <p className="responsive-font-size-3 is-bold-family">{t("noSearchMade", "НЯМАШ НАПРАВЕНА ПРОВЕРКА")}</p>
        <p
          style={{
            margin: " 0px 30px"
          }}
        >
          {t(
            "getInfoFastAndEasy",
            "Получи бързо и лесно информация за наличието на глоби от КАТ, както и за валидността на Гражданската отговорност, Техническия преглед и Винетката."
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="is-fullwidth">
      {gloSavedData.map((data, index) => {
        const { carNo, _id } = data;

        return (
          <Link className="is-fullwidth" to={`${basePath}/vignettes-and-services/fine-checks?id=${_id}`} key={index}>
            <div className={`${styles.myProfileActions} is-fullwidth`}>
              <div className="is-flex is-align-items-center ">
                <CheckDataIcon />
                <div className="ml-4">
                  <p>
                    {t("key14098", "Автомобил")}:<span className="is-bold-family"> {carNo}</span>
                  </p>
                </div>
              </div>
              <div className={styles.arrowCircle}>
                <ArrowRight className={styles.rightArrow} />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export const GloSingleInfo = ({ gloSavedData, t }) => {
  if (!gloSavedData) {
    return null;
  }

  const {
    consentGTP,
    consentGO,
    consentVignette,
    consentFines,
    lastVerifiedDateGO,
    lastVerifiedDateGTP,
    lastVerifiedDateFines,
    lastVerifiedDateVignette,
    lastVerifiedDate
  } = gloSavedData;
  const { carNo, vignetteValidTo, countFines, sumFines, goInsurer, vignettePrice, vignetteId, goValidTo, gtpValidTo, hasFines, goValidFrom, vignetteValidFrom } = gloSavedData;

  // Vig BreakDown
  const vigStatus = vignetteValidTo ? "success" : "warning";
  const vigSubtitle = vignetteValidTo ? t("activeVignette", "Имаш активна винетка!") : t("noActiveVignette", "Нямаш активна винетка!");
  const formattedVigValidFrom = moment(vignetteValidFrom).format("DD.MM.YYYY г.");
  const formattedVigValidTo = moment(vignetteValidTo).format("DD.MM.YYYY г.");
  const vigRowsData =
    vignetteValidTo && vignettePrice && vignetteId
      ? [
          t("validFromTo", {
            validFrom: formattedVigValidFrom,
            validTo: formattedVigValidTo
          }),
          t("vignetteId", { id: vignetteId })
        ]
      : [];

  // Chr BreakDown

  const chrStatus = gtpValidTo ? "success" : "warning";
  const chrSubtitle = gtpValidTo ? t("validTechnicalReview", "Имаш валиден технически преглед!") : t("noValidTechnicalReview", "Нямаш валиден технически преглед!");
  const formattedChrValidTo = moment(gtpValidTo).format("DD.MM.YYYY г.");
  const chrRowsData = gtpValidTo ? [t("validTo", { validTo: formattedChrValidTo })] : [];

  // GO BreakDown
  const goStatus = goValidTo ? "success" : "warning";
  const goSubtitle = goValidTo ? t("youHaveValidGo", "Имаш валидна Гражданска отговорност!") : t("notValidGo", "Нямаш валидна Гражданска отговорност!");
  const formattedGoValidFrom = moment(goValidFrom).format("DD.MM.YYYY г.");
  const formattedGoValidTo = moment(goValidTo).format("DD.MM.YYYY г.");
  const goRowsData =
    goValidTo && formattedGoValidTo && goInsurer
      ? [
          t("validFromTo", {
            validFrom: formattedGoValidFrom,
            validTo: formattedGoValidTo
          }),
          t("insurer", { insurer: goInsurer })
        ]
      : [];

  // GLO BreakDown
  const gloStatus = hasFines ? "warning" : "success";
  const gloSubtitle = hasFines ? t("youHaveFinesFromKat", "Имаш глоби от КАТ!") : t("noFinesFromKat", "Нямаш глоби от КАТ!");
  const lastVerifiedFinesDate = lastVerifiedDateFines || lastVerifiedDate;
  const lastVerifiedFinesYear = moment(lastVerifiedFinesDate).year();
  // Fines verified in 2025 or earlier were recorded in BGN, so they are
  // redenominated at the official fixed rate rather than just relabelled —
  // swapping the symbol alone would overstate an old fine by ~2x.
  const finesSumEur = lastVerifiedFinesYear > 2025 ? sumFines : sumFines / EUR_FIXED_RATE;
  const gloRowsData = hasFines && countFines && sumFines ? [t("fineCount", { count: countFines }), t("totalFineSum", { sum: finesSumEur.toFixed(2), currencySymbol: "€" })] : [];

  return (
    <div className="is-fullwidth is-flex is-flex-direction-column row-gap-18">
      <p className="has-text-centered mb-4">
        <span className="is-regular-font-family">{t("key14098", "Автомобил")}:</span> {carNo}
      </p>

      {consentVignette && (
        <GloInfoBox
          iconType="vig"
          title="ВИНЕТКА"
          subtitle={vigSubtitle}
          rowData={vigRowsData}
          status={vigStatus}
          lastVerified={lastVerifiedDateVignette || lastVerifiedDate}
          containerStyles={{ minWidth: "unset" }}
        />
      )}
      {consentGTP && (
        <GloInfoBox
          iconType="chr"
          title="ТЕХНИЧЕСКИ ПРЕГЛЕД"
          subtitle={chrSubtitle}
          rowData={chrRowsData}
          status={chrStatus}
          lastVerified={lastVerifiedDateGTP || lastVerifiedDate}
          containerStyles={{ minWidth: "unset" }}
        />
      )}
      {consentGO && (
        <GloInfoBox
          iconType="go"
          title="ГРАЖДАНСКА ОТГОВОРНОСТ"
          subtitle={goSubtitle}
          rowData={goRowsData}
          status={goStatus}
          lastVerified={lastVerifiedDateGO || lastVerifiedDate}
          containerStyles={{ minWidth: "unset" }}
        />
      )}
      {consentFines && (
        <GloInfoBox
          iconType="glo"
          title="ГЛОБИ ОТ КАТ"
          subtitle={gloSubtitle}
          rowData={gloRowsData}
          status={gloStatus}
          lastVerified={lastVerifiedDateFines || lastVerifiedDate}
          containerStyles={{ minWidth: "unset" }}
        />
      )}
    </div>
  );
};

const TableRow = ({ title, value }) => (
  <div className={styles.damageTableBoxRow}>
    <div className={styles.damageTableBoxLabel}>{title}:</div>
    <div>{value}</div>
  </div>
);

export const ChrSingleCar = ({ carDamageData, t }) => {
  if (!carDamageData) {
    return null;
  }

  const { extraData, timestamp } = carDamageData || {};

  const purchaseData = Array.isArray(carDamageData.purchaseData) ? carDamageData.purchaseData[0] : carDamageData.purchaseData;

  const { vinNumber, carNo } = purchaseData || {};
  const { AudaHistoryInfo, FraudCheckData } = extraData[0] || {};
  const { isFlagged } = FraudCheckData || {};
  const { TotalNumberOfClaims, Claims } = AudaHistoryInfo || {};

  const formattedDate = moment(timestamp).format("DD.MM.YYYY г.");

  return (
    <div className="is-flex is-flex-direction-column ">
      <p className="mx-auto">
        <span className="is-bold-family">{carNo ? `${t("key14098", "Автомобил")}: ` : t("VIN номер")}</span>
        {carNo || vinNumber}
      </p>

      <p className="mx-auto">
        <span className="is-bold-family">{t("toDate", "Към дата")}: </span>
        {formattedDate}
      </p>

      <div className={styles.carCrashBoxContainer}>
        <div className={styles.carCrashBox}>
          <p className="is-bold-family is-uppercase">{t("pages.common.damageInformation", "Информация за щети")}</p>
          <CarCrashIcon />
          <p style={{ fontSize: 13 }}>
            {t("thisCar", "Този автомобил")} <span className="is-bold-family">{TotalNumberOfClaims ? t("has", "ИМА") : t("hasNot", "НЯМА")}</span>
            {TotalNumberOfClaims
              ? t("discoveredInsuranceClaimsInEuropeDynamic", { totalNumberOfClaims: TotalNumberOfClaims })
              : t("discoveredInsuranceClaimsInEuropeStatick", " открити застрахователни щети в Европа!")}
          </p>

          <InfoIcon className="mt-auto" style={{ fill: "#D70505", minWidth: 29, minHeight: 29, maxWidth: 29, maxHeight: 29 }} />
        </div>

        <div className={styles.carCrashBox}>
          <p className="is-bold-family is-uppercase">{t("isDeclaredTotalLoss", "Обявен ли е за тотална щета")}</p>
          <CarVinIcon />
          <p style={{ fontSize: 13 }}>
            {t("thisCar", "Този автомобил")} <span className="is-bold-family">{isFlagged ? t("isDeclared", "Е ОБЯВЕН") : t("isNotDeclared", "НЕ Е")}</span>{" "}
            {t("declaredTotalLoss", "обявен за тотална щета.")}
          </p>

          <CheckIcon className="svg-standard-box-120 is-green mt-auto" style={{ minWidth: 29, minHeight: 29, maxWidth: 29, maxHeight: 29 }} />
        </div>
      </div>

      <p className="is-size-3 is-bold-family is-primary-color mx-auto mt-6">{t("descriptionOfDamages", "ОПИСАНИЕ НА ЩЕТИ")}</p>

      {Claims.map((claim, index) => {
        const claimDate = moment(claim.CalculationDateStr, "DD-MM-YYYY").locale("bg").format("MMMM YYYY");
        const formattedClaimDate = claimDate.charAt(0).toUpperCase() + claimDate.slice(1);
        const claimDamageValue = `${claim.DamageValue} ${getCurrencyByCode({ currencyCode: claim.ClaimCurrency })}`;
        const claimCountry = getCountriesByCode({ countryCode: claim.InsuranceCompanyCountry });

        return (
          <div className={styles.damageTableBox} key={index}>
            <div className={styles.damageTableBoxHeader}>
              {t("damageNumber", "ЩЕТА №")} {index + 1}
            </div>
            <div className={styles.damageTableBoxContent}>
              <TableRow title={t("date", "Дата")} value={formattedClaimDate} />
              <TableRow title={t("value", "Стойност")} value={claimDamageValue} />
              <TableRow title={t("country", "Държава")} value={claimCountry} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
